import React, { useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import Button from '../ui/Button';
import Card, { CardHeader, CardContent } from '../ui/Card';
import { Download, Upload, FileText, AlertCircle, CheckCircle } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useAppStore } from '../../store';

interface BulkTaskImportModalProps {
  isOpen: boolean;
  onClose: () => void;
  entityType: 'production' | 'sample';
  onImportComplete: () => void;
}

interface TaskRow {
  entityName: string;
  taskName: string;
  description?: string;
  assignedTo?: string;
  dueDate?: string;
  priority?: 'low' | 'medium' | 'high';
  estimatedHours?: number;
  cost?: number;
}

const BulkTaskImportModal: React.FC<BulkTaskImportModalProps> = ({
  isOpen,
  onClose,
  entityType,
  onImportComplete
}) => {
  const [file, setFile] = useState<File | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [importResults, setImportResults] = useState<{
    success: number;
    errors: string[];
    total: number;
  } | null>(null);
  const [previewData, setPreviewData] = useState<TaskRow[]>([]);
  const [showPreview, setShowPreview] = useState(false);

  const { productions, samples, suppliers, salespersons } = useAppStore();

  // Generar CSV de ejemplo
  const generateExampleCSV = () => {
    const headers = [
      'entityName',
      'taskName', 
      'description',
      'assignedTo',
      'dueDate',
      'priority',
      'estimatedHours',
      'cost'
    ];

    const exampleData = [
      [
        'Producción Ejemplo 1',
        'Corte de tela',
        'Cortar tela según patrones',
        'Juan Pérez',
        '2024-02-15',
        'high',
        '4',
        '150.00'
      ],
      [
        'Muestra Ejemplo 1',
        'Confección',
        'Confeccionar muestra según especificaciones',
        'María García',
        '2024-02-20',
        'medium',
        '6',
        '200.00'
      ]
    ];

    const csvContent = [
      headers.join(','),
      ...exampleData.map(row => row.map(field => `"${field}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `ejemplo_tareas_${entityType === 'production' ? 'producciones' : 'muestras'}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Procesar archivo CSV
  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const uploadedFile = event.target.files?.[0];
    if (uploadedFile && uploadedFile.type === 'text/csv') {
      setFile(uploadedFile);
      parseCSV(uploadedFile);
    } else {
      alert('Por favor selecciona un archivo CSV válido');
    }
  };

  // Parsear CSV
  const parseCSV = (file: File) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result as string;
      const lines = text.split('\n').filter(line => line.trim());
      
      if (lines.length < 2) {
        alert('El archivo CSV debe tener al menos una fila de datos');
        return;
      }

      const headers = lines[0].split(',').map(h => h.replace(/"/g, '').trim());
      const data: TaskRow[] = [];

      for (let i = 1; i < lines.length; i++) {
        const values = lines[i].split(',').map(v => v.replace(/"/g, '').trim());
        if (values.length >= 2) {
          data.push({
            entityName: values[0] || '',
            taskName: values[1] || '',
            description: values[2] || '',
            assignedTo: values[3] || '',
            dueDate: values[4] || '',
            priority: (values[5] as 'low' | 'medium' | 'high') || 'medium',
            estimatedHours: values[6] ? parseFloat(values[6]) : undefined,
            cost: values[7] ? parseFloat(values[7]) : undefined
          });
        }
      }

      setPreviewData(data);
      setShowPreview(true);
    };
    reader.readAsText(file);
  };

  // Importar tareas
  const handleImport = async () => {
    if (!file || previewData.length === 0) return;

    setIsProcessing(true);
    const errors: string[] = [];
    let successCount = 0;

    try {
      // Obtener todas las entidades (producciones o muestras)
      const entities = entityType === 'production' ? productions : samples;
      
      for (const taskData of previewData) {
        try {
          // Buscar la entidad por nombre
          const entity = entities.find(e => 
            e.name.toLowerCase().trim() === taskData.entityName.toLowerCase().trim()
          );

          if (!entity) {
            errors.push(`No se encontró ${entityType === 'production' ? 'producción' : 'muestra'} con nombre: ${taskData.entityName}`);
            continue;
          }

          // Buscar proveedor por nombre si se especifica
          let supplierId = null;
          if (taskData.assignedTo) {
            const supplier = suppliers.find(s => 
              s.name.toLowerCase().trim() === taskData.assignedTo!.toLowerCase().trim()
            );
            supplierId = supplier?.id || null;
          }

          // Crear la tarea
          const { error } = await supabase
            .from('production_tasks')
            .insert({
              entity_id: entity.id,
              entity_type: entityType,
              name: taskData.taskName,
              description: taskData.description || '',
              assigned_to: supplierId,
              due_date: taskData.dueDate ? new Date(taskData.dueDate).toISOString() : null,
              priority: taskData.priority || 'medium',
              estimated_hours: taskData.estimatedHours || null,
              cost: taskData.cost || null,
              status: 'pending'
            });

          if (error) {
            errors.push(`Error al crear tarea "${taskData.taskName}": ${error.message}`);
          } else {
            successCount++;
          }
        } catch (error) {
          errors.push(`Error procesando tarea "${taskData.taskName}": ${error}`);
        }
      }

      setImportResults({
        success: successCount,
        errors,
        total: previewData.length
      });

      if (successCount > 0) {
        onImportComplete();
      }
    } catch (error) {
      errors.push(`Error general: ${error}`);
      setImportResults({
        success: 0,
        errors,
        total: previewData.length
      });
    }

    setIsProcessing(false);
  };

  // Resetear modal
  const handleClose = () => {
    setFile(null);
    setPreviewData([]);
    setShowPreview(false);
    setImportResults(null);
    onClose();
  };

  return (
    <Dialog.Root open={isOpen} onOpenChange={handleClose}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/50 z-50" />
        <Dialog.Content className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white dark:bg-gray-800 rounded-lg shadow-xl z-50 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
          <div className="p-6">
            <div className="flex items-center justify-between mb-6">
              <Dialog.Title className="text-xl font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                <Upload className="h-5 w-5" />
                Importar Tareas Masivamente - {entityType === 'production' ? 'Producciones' : 'Muestras'}
              </Dialog.Title>
              <Dialog.Close asChild>
                <button className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
                  <X className="h-6 w-6" />
                </button>
              </Dialog.Close>
            </div>

        <div className="space-y-6">
          {/* Descargar ejemplo */}
          <Card>
            <CardHeader 
              title="Paso 1: Descargar Archivo de Ejemplo"
              action={
                <div className="flex items-center gap-2">
                  <FileText className="h-5 w-5" />
                </div>
              }
            />
            <CardContent>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                Descarga el archivo CSV de ejemplo para ver el formato correcto. 
                La primera columna debe contener el nombre exacto de la {entityType === 'production' ? 'producción' : 'muestra'}.
              </p>
              <Button
                onClick={generateExampleCSV}
                variant="outline"
                className="flex items-center gap-2"
              >
                <Download className="h-4 w-4" />
                Descargar CSV de Ejemplo
              </Button>
            </CardContent>
          </Card>

          {/* Subir archivo */}
          <Card>
            <CardHeader 
              title="Paso 2: Subir Archivo CSV"
              action={
                <div className="flex items-center gap-2">
                  <Upload className="h-5 w-5" />
                </div>
              }
            />
            <CardContent>
              <div className="space-y-4">
                <input
                  type="file"
                  accept=".csv"
                  onChange={handleFileUpload}
                  className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                />
                
                {file && (
                  <div className="flex items-center gap-2 text-sm text-green-600">
                    <CheckCircle className="h-4 w-4" />
                    Archivo seleccionado: {file.name}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Vista previa */}
          {showPreview && previewData.length > 0 && (
            <Card>
              <CardHeader title="Vista Previa de Datos" />
              <CardContent>
                <div className="space-y-4">
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Se encontraron {previewData.length} tareas para importar:
                  </p>
                  
                  <div className="max-h-60 overflow-y-auto border rounded-lg">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 dark:bg-gray-800 sticky top-0">
                        <tr>
                          <th className="px-3 py-2 text-left">Entidad</th>
                          <th className="px-3 py-2 text-left">Tarea</th>
                          <th className="px-3 py-2 text-left">Asignado</th>
                          <th className="px-3 py-2 text-left">Fecha</th>
                          <th className="px-3 py-2 text-left">Prioridad</th>
                        </tr>
                      </thead>
                      <tbody>
                        {previewData.slice(0, 10).map((row, index) => (
                          <tr key={index} className="border-t">
                            <td className="px-3 py-2">{row.entityName}</td>
                            <td className="px-3 py-2">{row.taskName}</td>
                            <td className="px-3 py-2">{row.assignedTo || '-'}</td>
                            <td className="px-3 py-2">{row.dueDate || '-'}</td>
                            <td className="px-3 py-2">{row.priority || '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {previewData.length > 10 && (
                      <p className="text-xs text-gray-500 p-2 text-center">
                        ... y {previewData.length - 10} tareas más
                      </p>
                    )}
                  </div>

                  <div className="flex gap-2">
                    <Button
                      onClick={handleImport}
                      disabled={isProcessing}
                      className="flex items-center gap-2"
                    >
                      {isProcessing ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                          Importando...
                        </>
                      ) : (
                        <>
                          <Upload className="h-4 w-4" />
                          Importar {previewData.length} Tareas
                        </>
                      )}
                    </Button>
                    
                    <Button
                      variant="outline"
                      onClick={() => setShowPreview(false)}
                    >
                      Cancelar
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Resultados */}
          {importResults && (
            <Card>
              <CardHeader 
                title="Resultados de la Importación"
                action={
                  <div className="flex items-center gap-2">
                    {importResults.success > 0 ? (
                      <CheckCircle className="h-5 w-5 text-green-500" />
                    ) : (
                      <AlertCircle className="h-5 w-5 text-red-500" />
                    )}
                  </div>
                }
              />
              <CardContent>
                <div className="space-y-4">
                  <div className="grid grid-cols-3 gap-4 text-center">
                    <div className="bg-green-50 dark:bg-green-900/20 p-3 rounded-lg">
                      <div className="text-2xl font-bold text-green-600">{importResults.success}</div>
                      <div className="text-sm text-green-600">Exitosas</div>
                    </div>
                    <div className="bg-red-50 dark:bg-red-900/20 p-3 rounded-lg">
                      <div className="text-2xl font-bold text-red-600">{importResults.errors.length}</div>
                      <div className="text-sm text-red-600">Con Error</div>
                    </div>
                    <div className="bg-gray-50 dark:bg-gray-800 p-3 rounded-lg">
                      <div className="text-2xl font-bold text-gray-600">{importResults.total}</div>
                      <div className="text-sm text-gray-600">Total</div>
                    </div>
                  </div>

                  {importResults.errors.length > 0 && (
                    <div className="space-y-2">
                      <h4 className="font-medium text-red-600">Errores encontrados:</h4>
                      <div className="max-h-32 overflow-y-auto bg-red-50 dark:bg-red-900/20 p-3 rounded-lg">
                        {importResults.errors.map((error, index) => (
                          <div key={index} className="text-sm text-red-600 flex items-start gap-2">
                            <X className="h-4 w-4 mt-0.5 flex-shrink-0" />
                            {error}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="flex justify-end">
                    <Button onClick={handleClose}>
                      Cerrar
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

export default BulkTaskImportModal;
